import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import http from '@/services/http';

export type DynamoDbTableStatRow = {
  table_name: string;
  item_count: number;
  size_bytes: number;
  captured_at: string;
};

export type DynamoDbTableStatsLatestResponse = {
  captured_at: string | null;
  tables: DynamoDbTableStatRow[];
};

export type DynamoDbTableStatsHistoryResponse = {
  rows: DynamoDbTableStatRow[];
};

export function useDynamoDbTableStatsLatest() {
  return useQuery({
    queryKey: ['dynamo-db-table-statistics', 'latest'],
    queryFn: () =>
      http
        .get<DynamoDbTableStatsLatestResponse>('/api/dynamo-db-table-statistics/latest')
        .then((response) => response.data),
  });
}

export function useDynamoDbTableStatsHistory(
  days: MaybeRefOrGetter<number>,
  tableName: MaybeRefOrGetter<string>
) {
  return useQuery({
    queryKey: computed(() => [
      'dynamo-db-table-statistics',
      'history',
      toValue(days),
      toValue(tableName),
    ]),
    queryFn: () => {
      const dayCount = toValue(days);
      const name = toValue(tableName).trim();
      const params = new URLSearchParams({ days: String(dayCount) });
      if (name) {
        params.set('table_name', name);
      }
      return http
        .get<DynamoDbTableStatsHistoryResponse>(
          `/api/dynamo-db-table-statistics/history?${params.toString()}`
        )
        .then((response) => response.data);
    },
  });
}

export function useDynamoDbTableNameOptions(
  latestTables: MaybeRefOrGetter<DynamoDbTableStatRow[] | undefined>
) {
  return computed(() => {
    const tables = toValue(latestTables) ?? [];
    const names = [...new Set(tables.map((row) => row.table_name))];
    names.sort((a, b) => a.localeCompare(b));
    return names;
  });
}
